import { useState } from 'react';
import { useAppDispatch, useAppSelector } from '@/state/store';
import { toggleDebugMenu, setCurrentlyViewingReward } from '@/state/features/app';
import Modal from '@/components/Modal';
import { useTheme } from '@/theme';
import * as arise from '@/arise/api';
import { clearProfileV2FromLocalStorage } from '@/utils/localProfileV2';
import { clearLocalVideoProgressFromStorage } from '@/utils/localVideoProgress';
import { clearSavedLangIso } from '@/utils/localLang';
import { appVersion } from '@/utils/version';

import * as styles from './styles.module.scss';

export default function AriseDebugMenu() {
    const dispatch = useAppDispatch();
    const isDebugMenuOpen = useAppSelector((state) => state.app.isDebugMenuOpen);
    const currentUserProfileV2 = useAppSelector((state) => state.profile.currentUserProfileV2);
    const location = useAppSelector((state) => state.app.location);
    const theme = useTheme();
    const [error, setError] = useState<string | null>();
    const [iframeEventName, setIframeEventName] = useState('');
    const [iframeEventPayload, setIframeEventPayload] = useState('');

    const handleSimulateIframeEvent = () => {
        setError(null);
        try {
            // Parse payload if it's JSON, otherwise use as string
            let parsedPayload = iframeEventPayload;
            try {
                parsedPayload = JSON.parse(iframeEventPayload);
            } catch {
                // Keep as string if not valid JSON
            }

            console.log('Simulating iFrame message event:', {
                type: iframeEventName,
                payload: parsedPayload,
            });

            // Simulate a message event from an iframe (handled by AriseBridgeHost)
            const messageEvent = new MessageEvent('message', {
                data: {
                    type: iframeEventName,
                    payload: parsedPayload,
                },
                origin: window.location.origin,
            });
            window.dispatchEvent(messageEvent);

            console.log(`MessageEvent "${iframeEventName}" dispatched with payload:`, parsedPayload);
        } catch (err) {
            setError('Failed to simulate event: ' + (err instanceof Error ? err.message : String(err)));
        }
    };

    const handleSimulateIframeEventAfterDelay = () => {
        dispatch(toggleDebugMenu());
        setTimeout(() => {
            handleSimulateIframeEvent();
        }, 2000);
    };

    const handleClearLocalProfile = () => {
        setError(null);
        try {
            clearProfileV2FromLocalStorage();
        } catch (err) {
            setError('Failed to clear local profile');
        }
    };

    const handleClearRemoteProfile = async () => {
        setError(null);
        try {
            await Promise.all([
                arise.resetProfileBadges(),
                arise.resetProfileTitles(),
                arise.resetProfileMissionCompleted(),
            ]);
        } catch (err) {
            setError('Failed to clear remote profile');
        }
    };

    const handleClearProfile = async () => {
        setError(null);
        try {
            clearProfileV2FromLocalStorage();
            await Promise.all([
                arise.resetProfileBadges(),
                arise.resetProfileTitles(),
                arise.resetProfileMissionCompleted(),
            ]);
        } catch (err) {
            setError('Failed to clear profile');
        }
    };

    const handleClearLocalVideoData = async () => {
        setError(null);
        try {
            clearLocalVideoProgressFromStorage();
        } catch (err) {
            setError('Failed to clear local video data');
        }
    };

    const handleClearSavedLang = () => {
        setError(null);
        try {
            clearSavedLangIso();
        } catch (err) {
            setError('Failed to clear saved language');
        }
    };

    const handleClearLocalAuthToken = () => {
        setError(null);
        try {
            window.localStorage.removeItem('ARISEENGINE_AUTHTOKEN');
        } catch (err) {
            setError('Failed to clear auth token');
        }
    };

    const handleClearAllLocalStorage = () => {
        setError(null);
        try {
            window.localStorage.clear();
        } catch (err) {
            setError('Failed to clear local storage');
        }
    };

    const getArisewareData = () => {
        const iframeURL = location?.iframeURL || location?.latestArisewareBuildURL;
        if (!iframeURL) return 'No Ariseware';
        try {
            const arisewareVersionDateTime = iframeURL.match(/\/(\d+)\//)[1];
            const dateVersion = new Date(parseInt(arisewareVersionDateTime)).toLocaleString();
            return `${dateVersion} (${arisewareVersionDateTime})`;
        } catch (error) {
            return iframeURL || 'No Ariseware';
        }
    };

    return (
        <Modal
            title="Debug Menu"
            isVisible={isDebugMenuOpen}
            onClose={() => dispatch(toggleDebugMenu())}
            hasCloseButton={true}
            modalClassname={styles.AriseDebugMenu}
            zIndex={2000}
            canScroll
            leaveSpaceAboveAndBelow={false}
            shouldCloseOnExternalClick={false}
        >
            {error && (
                <div className={styles.errorMessage}>
                    <strong>Error:</strong> {error}
                </div>
            )}
            <section>
                <h3>System Info</h3>
                <ul>
                    <li>
                        <strong>Arise Frontend version: </strong>
                        {appVersion}
                    </li>
                    <li>
                        <strong>Ariseware version:</strong> {getArisewareData()}
                    </li>
                    <li>
                        <strong>User ID:</strong> {currentUserProfileV2?.userId}
                    </li>
                    <li>
                        <strong>Auth token:</strong> {window.localStorage.getItem('ARISEENGINE_AUTHTOKEN') || ''}
                    </li>
                </ul>
            </section>
            <section>
                <h3>Profile Actions</h3>
                <p>Please reload after clearing</p>
                <button onClick={handleClearLocalProfile}>Clear Local Profile</button>
                <button onClick={handleClearRemoteProfile}>Clear Remote Profile</button>
                <button onClick={handleClearProfile}>Clear All Profile Data</button>
            </section>
            <section>
                <h3>Features</h3>
                <p>Please reload after clearing video data</p>
                <button onClick={handleClearLocalVideoData}>Clear Local Video Data</button>
                <button
                    onClick={() => {
                        dispatch(toggleDebugMenu());
                        setTimeout(() => {
                            dispatch(
                                setCurrentlyViewingReward({
                                    id: '123',
                                    createdAt: '2026-05-18T08:16:50.693Z',
                                    universeId: '456',
                                    modalTitle: 'Congrats!',
                                    name: 'Prize',
                                    description: 'Ut enim ad minim veniam, quis nostrud exercitation ullamco.',
                                    mainImagePath: 'uploads/97cab966-aa05-4e90-8b0b-f1448c318de5/16x9.webp',
                                    secondaryImagePath: 'uploads/97cab966-aa05-4e90-8b0b-f1448c318de5/16x9.webp',
                                    redemptionInstructions: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
                                    sfxUrl: null,
                                    externalUrl: 'https://thosebeyond.io',
                                    readMoreUrl: 'https://thosebeyond.io',
                                    readMoreText: 'Read more',
                                    isFiniteSupply: true,
                                    oneCodeForAll: false,
                                    messageText: "Here's your code!",
                                    redemptionType: 'code',
                                    redemptionValue: 'code-1234',
                                    ctaText: 'CTA',
                                    ctaMethod: 'open_link',
                                }),
                            );
                        }, 2000);
                    }}
                >
                    View Code Reward Modal
                </button>
            </section>
            <section>
                <h3>User</h3>
                <p>Please reload after clearing</p>
                <button onClick={handleClearLocalAuthToken}>Clear Local Auth Token</button>
                <button onClick={handleClearSavedLang}>Clear Saved Language</button>
                <button onClick={handleClearAllLocalStorage}>Clear All Local Storage Data</button>
            </section>
            <section>
                <h3>Simulate event from iFrame</h3>
                <div className={styles.formGroup}>
                    <label>Event name:</label>
                    <input
                        type="text"
                        placeholder="e.g. open-url"
                        value={iframeEventName}
                        onChange={(e) => setIframeEventName(e.target.value)}
                    />
                </div>
                <div className={styles.formGroup}>
                    <label>Payload detail (JSON or string):</label>
                    <textarea
                        placeholder="e.g. https://www.google.com"
                        value={iframeEventPayload}
                        onChange={(e) => setIframeEventPayload(e.target.value)}
                        rows={3}
                    />
                </div>
                <button onClick={handleSimulateIframeEvent}>Send</button>
                <button onClick={handleSimulateIframeEventAfterDelay}>Close Debug, wait 5 seconds and then Send</button>
            </section>
        </Modal>
    );
}
